import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { vectorExportTrackingAtom } from '@/atoms/vectorExport-progress.atom';
import { execute } from '@/graphql/execute';
import type { ExportCrs, VectorExportFormat } from '@/graphql/graphql';
import { startVectorExport } from '@/queries/vectorExport.query';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  layerId: string;
  layerName: string;
  /** Selected feature ids; empty/undefined ⇒ export the whole layer. */
  featureIds?: string[];
  /** Best-effort: true when the layer mixes geometry types (disables SHP).
   *  Undefined ⇒ unknown (let the backend validate). */
  mixedGeometry?: boolean;
  /** Format pre-selected from the clicked dropdown item. */
  initialFormat: VectorExportFormat;
}

const FORMATS: VectorExportFormat[] = ['SHP', 'DXF', 'DWG', 'GPKG'];

export default function VectorExportDialog({
  open,
  onOpenChange,
  projectId,
  layerId,
  layerName,
  featureIds,
  mixedGeometry,
  initialFormat,
}: Props) {
  const { t } = useTranslation('project');
  const [format, setFormat] = useState<VectorExportFormat>(initialFormat);
  const [crs, setCrs] = useState<ExportCrs>('PROJECT');
  const [name, setName] = useState(layerName);
  const [, setTrackers] = useAtom(vectorExportTrackingAtom);

  useEffect(() => {
    if (!open) return;
    setFormat(initialFormat);
    setCrs('PROJECT');
    setName(layerName);
  }, [open, initialFormat, layerName]);

  const shpDisabled = mixedGeometry === true;
  const selectedCount = featureIds?.length ?? 0;

  const mutation = useMutation({
    mutationFn: async () =>
      execute(startVectorExport, {
        input: {
          layerId,
          featureIds: featureIds && featureIds.length > 0 ? featureIds : null,
          format,
          crs,
          label: name.trim(),
        },
      }),
    onSuccess: (data) => {
      const batchId = data.startVectorExport;
      const toastId = toast.loading(t('vectorExport.runningToast', { name: layerName }), { duration: Infinity });
      setTrackers((prev) => [
        ...prev,
        { toastId, batchId, projectId, sourceLayerId: layerId, sourceLayerName: layerName, format },
      ]);
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message || t('vectorExport.failedToStart')),
  });

  const isFormValid = name.trim() !== '' && !(format === 'SHP' && shpDisabled);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('vectorExport.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <div className='min-w-0 space-y-4 py-2'>
          <p className='text-xs text-muted-foreground'>
            {selectedCount > 0
              ? t('vectorExport.scopeSelected', { count: selectedCount })
              : t('vectorExport.scopeAll', { name: layerName })}
          </p>

          {/* Format */}
          <div className='space-y-2'>
            <Label>{t('vectorExport.formatLabel')}</Label>
            <Select
              value={format}
              onValueChange={(v) => setFormat(v as VectorExportFormat)}
              disabled={mutation.isPending}>
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMATS.map((f) => (
                  <SelectItem key={f} value={f} disabled={f === 'SHP' && shpDisabled}>
                    {f}
                    {f === 'SHP' && shpDisabled ? ` — ${t('vectorExport.shpMixedShort')}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {format === 'SHP' && shpDisabled && (
              <p className='text-xs text-amber-500'>{t('vectorExport.shpMixedHint')}</p>
            )}
          </div>

          {/* CRS */}
          <div className='space-y-2'>
            <Label>{t('vectorExport.crsLabel')}</Label>
            <RadioGroup
              value={crs}
              onValueChange={(v) => setCrs(v as ExportCrs)}
              className='flex gap-4'
              disabled={mutation.isPending}>
              <label className='flex cursor-pointer items-center gap-2'>
                <RadioGroupItem value='PROJECT' id='crs-project' />
                <span className='text-sm'>{t('vectorExport.crsProject')}</span>
              </label>
              <label className='flex cursor-pointer items-center gap-2'>
                <RadioGroupItem value='WGS84' id='crs-wgs84' />
                <span className='text-sm'>{t('vectorExport.crsWgs84')}</span>
              </label>
            </RadioGroup>
          </div>

          {/* Name */}
          <div className='space-y-2'>
            <Label>{t('vectorExport.nameLabel')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={mutation.isPending} />
          </div>

          <p className='text-xs text-muted-foreground'>{t('vectorExport.infoNote')}</p>
        </div>

        <DialogFooter>
          <Button variant='outline' type='button' onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t('vectorExport.cancel')}
          </Button>
          <Button type='button' onClick={() => mutation.mutate()} disabled={mutation.isPending || !isFormValid}>
            {t('vectorExport.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
